// Frontend-only value sets for the scan surface. See enums/shell.ts for the rationale.

export const ESTATE_LABEL_PLACEMENTS = ["above", "below"] as const;
export type EstateLabelPlacement = (typeof ESTATE_LABEL_PLACEMENTS)[number];
export const ESTATE_LABEL_PLACEMENT = {
  ABOVE: "above",
  BELOW: "below",
} as const satisfies Record<string, EstateLabelPlacement>;

export const EXECUTIVE_SUMMARY_MODULES = [
  "scan",
  "map",
  "brief",
  "govern",
] as const;
export type ExecutiveSummaryModule = (typeof EXECUTIVE_SUMMARY_MODULES)[number];
export const EXECUTIVE_SUMMARY_MODULE = {
  SCAN: "scan",
  MAP: "map",
  BRIEF: "brief",
  GOVERN: "govern",
} as const satisfies Record<string, ExecutiveSummaryModule>;

export const ISSUE_PAGE_MATCHS = [
  "id",
  "path",
  "path_without_query",
  "missing",
] as const;
export type IssuePageMatch = (typeof ISSUE_PAGE_MATCHS)[number];
export const ISSUE_PAGE_MATCH = {
  ID: "id",
  PATH: "path",
  PATH_WITHOUT_QUERY: "path_without_query",
  MISSING: "missing",
} as const satisfies Record<string, IssuePageMatch>;

export const JOURNEY_ROLES = [
  "entry",
  "hub",
  "content",
  "action",
  "destination",
  "unclassified",
] as const;
export type JourneyRole = (typeof JOURNEY_ROLES)[number];
export const JOURNEY_ROLE = {
  ENTRY: "entry",
  HUB: "hub",
  CONTENT: "content",
  ACTION: "action",
  DESTINATION: "destination",
  UNCLASSIFIED: "unclassified",
} as const satisfies Record<string, JourneyRole>;

export const JOURNEY_FRICTION_KINDS = [
  "broken_link",
  "journey_issue",
  "no_onward_link",
  "partial_coverage",
] as const;
export type JourneyFrictionKind = (typeof JOURNEY_FRICTION_KINDS)[number];
export const JOURNEY_FRICTION_KIND = {
  BROKEN_LINK: "broken_link",
  JOURNEY_ISSUE: "journey_issue",
  NO_ONWARD_LINK: "no_onward_link",
  PARTIAL_COVERAGE: "partial_coverage",
} as const satisfies Record<string, JourneyFrictionKind>;

export const PRIORITY_FLOW_TIERS = [
  "critical",
  "high",
  "review",
  "context",
] as const;
export type PriorityFlowTier = (typeof PRIORITY_FLOW_TIERS)[number];
export const PRIORITY_FLOW_TIER = {
  CRITICAL: "critical",
  HIGH: "high",
  REVIEW: "review",
  CONTEXT: "context",
} as const satisfies Record<string, PriorityFlowTier>;

export const CONTENT_FITNESS_ORIGINS = ["summary", "scorecard"] as const;
export type ContentFitnessOrigin = (typeof CONTENT_FITNESS_ORIGINS)[number];
export const CONTENT_FITNESS_ORIGIN = {
  SUMMARY: "summary",
  SCORECARD: "scorecard",
} as const satisfies Record<string, ContentFitnessOrigin>;

export const QUICK_LOOK_EMPHASISS = ["primary", "supporting"] as const;
export type QuickLookEmphasis = (typeof QUICK_LOOK_EMPHASISS)[number];
export const QUICK_LOOK_EMPHASIS = {
  PRIMARY: "primary",
  SUPPORTING: "supporting",
} as const satisfies Record<string, QuickLookEmphasis>;

export const PAGE_SORT_KEYS = [
  "priority",
  "score-asc",
  "score-desc",
  "issues-desc",
  "name",
  "cluster",
] as const;
export type PageSortKey = (typeof PAGE_SORT_KEYS)[number];
export const PAGE_SORT_KEY = {
  PRIORITY: "priority",
  SCORE_ASC: "score-asc",
  SCORE_DESC: "score-desc",
  ISSUES_DESC: "issues-desc",
  NAME: "name",
  CLUSTER: "cluster",
} as const satisfies Record<string, PageSortKey>;

export const SORT_DIRECTIONS = ["asc", "desc"] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];
export const SORT_DIRECTION = {
  ASC: "asc",
  DESC: "desc",
} as const satisfies Record<string, SortDirection>;

export const QUICK_LOOK_SOURCES = ["summary", "issues"] as const;
export type QuickLookSource = (typeof QUICK_LOOK_SOURCES)[number];
export const QUICK_LOOK_SOURCE = {
  SUMMARY: "summary",
  ISSUES: "issues",
} as const satisfies Record<string, QuickLookSource>;

export const SCAN_AVAILABILITY_KEYS = [
  "not_started",
  "in_progress",
  "scored",
  "partial",
  "failed_previous_score",
  "failed_no_score",
  "completed_without_score",
] as const;
export type ScanAvailabilityKey = (typeof SCAN_AVAILABILITY_KEYS)[number];
export const SCAN_AVAILABILITY_KEY = {
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  SCORED: "scored",
  PARTIAL: "partial",
  FAILED_PREVIOUS_SCORE: "failed_previous_score",
  FAILED_NO_SCORE: "failed_no_score",
  COMPLETED_WITHOUT_SCORE: "completed_without_score",
} as const satisfies Record<string, ScanAvailabilityKey>;

export const SCAN_ICON_NAMES = [
  "summary",
  "pages",
  "issues",
  "runs",
  "signals",
  "journeys",
  "ai",
  "brand",
] as const;
export type ScanIconName = (typeof SCAN_ICON_NAMES)[number];
export const SCAN_ICON_NAME = {
  SUMMARY: "summary",
  PAGES: "pages",
  ISSUES: "issues",
  RUNS: "runs",
  SIGNALS: "signals",
  JOURNEYS: "journeys",
  AI: "ai",
  BRAND: "brand",
} as const satisfies Record<string, ScanIconName>;

export const SCAN_SECTION_KEYS = [
  "runs",
  "summary",
  "pages",
  "issues",
  "signals",
  "ai-reference",
  "brand-guidelines",
  "help",
] as const;
export type ScanSectionKey = (typeof SCAN_SECTION_KEYS)[number];
export const SCAN_SECTION_KEY = {
  RUNS: "runs",
  SUMMARY: "summary",
  PAGES: "pages",
  ISSUES: "issues",
  SIGNALS: "signals",
  AI_REFERENCE: "ai-reference",
  BRAND_GUIDELINES: "brand-guidelines",
  HELP: "help",
} as const satisfies Record<string, ScanSectionKey>;

export const SIGNAL_MODULES = [
  "overview",
  "scan",
  "runs",
  "social",
  "map",
  "brief",
  "govern",
  "settings",
] as const;
export type SignalModule = (typeof SIGNAL_MODULES)[number];
export const SIGNAL_MODULE = {
  OVERVIEW: "overview",
  SCAN: "scan",
  RUNS: "runs",
  SOCIAL: "social",
  MAP: "map",
  BRIEF: "brief",
  GOVERN: "govern",
  SETTINGS: "settings",
} as const satisfies Record<string, SignalModule>;

export const SUMMARY_QUICK_LOOKS = ["fitness", "brand-guidelines"] as const;
export type SummaryQuickLook = (typeof SUMMARY_QUICK_LOOKS)[number];
export const SUMMARY_QUICK_LOOK = {
  FITNESS: "fitness",
  BRAND_GUIDELINES: "brand-guidelines",
} as const satisfies Record<string, SummaryQuickLook>;

export const BRAND_TABS = [
  "overview",
  "voice",
  "strategy",
  "channels",
  "sources",
  "activity",
] as const;
export type BrandTab = (typeof BRAND_TABS)[number];
export const BRAND_TAB = {
  OVERVIEW: "overview",
  VOICE: "voice",
  STRATEGY: "strategy",
  CHANNELS: "channels",
  SOURCES: "sources",
  ACTIVITY: "activity",
} as const satisfies Record<string, BrandTab>;

export const FLOWS_VIEWS = ["structure", "paths"] as const;
export type FlowsView = (typeof FLOWS_VIEWS)[number];
export const FLOWS_VIEW = {
  STRUCTURE: "structure",
  PATHS: "paths",
} as const satisfies Record<string, FlowsView>;
